import { cn } from "@/lib/utils";
import {
  freshnessLabel,
  getFreshnessKind,
  type FreshnessKind,
} from "@/lib/freshness";
import type { ArticleListItem } from "@/types/api";

interface FreshnessBadgeProps {
  article: Pick<ArticleListItem, "publish_at" | "created_at">;
  className?: string;
  /** Sempre mostra algo (relative) mesmo fora das 48h */
  fallbackRelative?: boolean;
  /** Para fundos escuros (barra de plantão) */
  tone?: "default" | "onDark";
}

const KIND_STYLES: Record<Exclude<FreshnessKind, null>, string> = {
  now: "bg-brand-tertiary text-white",
  hot: "bg-amber-400 text-black",
  recent: "bg-brand-chrome text-white",
};

const KIND_STYLES_ON_DARK: Record<Exclude<FreshnessKind, null>, string> = {
  now: "bg-brand-tertiary text-white",
  hot: "bg-amber-400 text-black",
  recent: "bg-white/15 text-white",
};

export function FreshnessBadge({
  article,
  className,
  fallbackRelative = false,
  tone = "default",
}: FreshnessBadgeProps) {
  const kind = getFreshnessKind(article);
  if (!kind && !fallbackRelative) return null;

  const label = freshnessLabel(article);
  if (!label) return null;

  const hot = kind === "now" || kind === "hot";
  const styles = tone === "onDark" ? KIND_STYLES_ON_DARK : KIND_STYLES;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded px-2 py-0.5 font-display text-[10px] font-bold uppercase tracking-wider",
        kind
          ? styles[kind]
          : tone === "onDark"
            ? "bg-white/15 text-white"
            : "bg-brand-surface-container text-brand-on-surface-variant",
        className,
      )}
    >
      {hot ? (
        <span
          className="freshness-pulse h-1.5 w-1.5 shrink-0 rounded-full bg-current"
          aria-hidden
        />
      ) : null}
      {label}
    </span>
  );
}
